import { useEffect, useState } from 'react';
import striptags from 'striptags';
import { useReduxState } from 'hooks';
import { DropdownSelectable } from 'components/common/dropdown';
import { editorSocket } from 'components/editor/socket';

import type {
  Block,
  BlockHeading,
  BlockList,
  BlockQuote,
  BlockText,
  Course
} from 'shared-types';

type BlockType = 'heading' | 'list' | 'quote' | 'text';

type BlockWithText = BlockText | BlockHeading | BlockQuote;

type BlockWithList = BlockList;

interface Option {
  text: string;
  value: BlockType;
}

interface Props {
  blockId: Block['id'];
  blockType: BlockType;
  data: Block['data'];
  languageVersion: Course['language'];
}

const EditorBlockConverter = ({ blockId, blockType, data }: Props) => {
  const { messages } = useReduxState();

  const [options, setOptions] = useState<Option[] | null>(null);

  useEffect(() => {
    const header: Option = { text: messages.blocksHeading, value: 'heading' };
    const list: Option = { text: messages.blocksList, value: 'list' };
    const quote: Option = { text: messages.blocksQuote, value: 'quote' };
    const text: Option = { text: messages.blocksText, value: 'text' };

    if (blockType === 'heading') {
      setOptions([header, text]);
    } else if (blockType === 'list') {
      setOptions([list, text]);
    } else if (blockType === 'quote') {
      setOptions([quote, text]);
    } else if (blockType === 'text') {
      setOptions([header, list, quote, text]);
    }
  }, [blockType]);

  const getConvertedData = (
    oldBlockType: BlockType,
    newBlockType: BlockType,
    data: Block['data']
  ) => {
    // update all locales with new block type data
    const getLocales = (fromType: string, targetType: string) => {
      return Object.entries(data.locales).reduce((acc, [key]) => {
        // allowed tags for marks we support including directly
        const allowedTags = ['strong', 'em', 'u', 'a', 'code'];

        const blockTextContent =
          (data as BlockWithText).locales[key]?.translatable?.text || '';

        const blockListHtml =
          (data as BlockWithList).locales[key]?.translatable?.listHtml || '';

        const translatable =
          targetType === 'heading'
            ? {
                text: striptags(blockTextContent).replace(/(\r\n|\n|\r)/g, '')
              }
            : targetType === 'list'
            ? {
                listHtml: blockTextContent
                  ? `<ul style="list-style-type:disc">${striptags(
                      blockTextContent,
                      allowedTags,
                      '\n'
                    )
                      .split('\n')
                      .filter((item) => item)
                      .map((item) => `<li><p>${item}</p></li>`)
                      .join('')}</ul>`
                  : ''
              }
            : targetType === 'quote'
            ? {
                attribution: '',
                text: blockTextContent
              }
            : fromType === 'heading' && targetType === 'text'
            ? {
                text: striptags(blockTextContent).replace(/(\r\n|\n|\r)/g, '')
              }
            : fromType === 'list' && targetType === 'text'
            ? {
                text: striptags(blockListHtml, [...allowedTags, 'p'], '')
              }
            : fromType === 'quote' && targetType === 'text'
            ? {
                text: blockTextContent
              }
            : {
                text: ''
              };

        acc[key] = { translatable };
        return acc;
      }, {});
    };

    const handlers = {
      text: {
        heading: () => ({
          format: 'html',
          locales: getLocales('text', 'heading'),
          text: '{{ locales.translatable.text }}'
        }),
        list: () => ({
          format: 'html',
          listHtml: '{{ locales.translatable.listHtml }}',
          locales: getLocales('text', 'list')
        }),
        quote: () => ({
          attribution: '{{ locales.translatable.attribution }}',
          format: 'html',
          locales: getLocales('text', 'quote'),
          text: '{{ locales.translatable.text }}'
        })
      },
      heading: {
        text: () => ({
          format: 'html',
          locales: getLocales('heading', 'text'),
          text: '{{ locales.translatable.text }}'
        })
      },
      list: {
        text: () => ({
          format: 'html',
          locales: getLocales('list', 'text'),
          text: '{{ locales.translatable.text }}'
        })
      },
      quote: {
        text: () => ({
          format: 'html',
          locales: getLocales('quote', 'text'),
          text: '{{ locales.translatable.text }}'
        })
      }
    };

    return handlers[oldBlockType][newBlockType]();
  };

  const handleTypeChange = (newType: BlockType) => {
    if (newType === blockType) return;

    editorSocket.updateBlockType({
      blockId,
      blockType: newType,
      data: getConvertedData(blockType, newType, data)
    });
  };

  return (
    <DropdownSelectable
      align="left"
      category="lighter"
      defaultText={messages.editorBlockConverterChangeType}
      defaultValue={blockType}
      onChange={handleTypeChange}
      options={options}
      size="small"
      style={{ marginRight: 10 }}
      value={blockType}
    />
  );
};

export default EditorBlockConverter;
